import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Result} from "../interfaces/result";
import {DecimalPipe, NgIf} from "@angular/common";
import {DeviceMatrix} from "../interfaces/device-matrix";
import {Thumb} from "../interfaces/thumb";
import {Optimierung} from "../interfaces/configuration";
import {ConfigurationAdaptions} from "../interfaces/configuration-adaptions";

/**
 * Component for the entry of the moderat. Let the user select its interests.
 */
@Component({
  selector: 'app-photovoltaikcheck-ergebnis-image-solar',
  templateUrl: './ergebnis.image-solar.component.html',
  providers: [NgIf, DecimalPipe]
})
export class ErgebnisImageSolarComponent implements OnInit, OnChanges {
  /**
   * The result as given from the server.
   */
  @Input() result: Result;

  /**
   * current configuration that should be visible
   */
  @Input() currentConfigurationType: string;

  /**
   * toggle if internal portal or not
   */
  @Input() internalPortal: boolean;

  /**
   * Indicates whether an element or component is hidden. Can be used to remove clickable elements
   *
   */
  @Input() isHidden: boolean = false;

  /**
   * Eventemitter if the area of the two parts changes
   */
  @Output() areaChanged: EventEmitter<ConfigurationAdaptions> = new EventEmitter();

  /**
   * the svg
   */
  @ViewChild('svg') svgElement: ElementRef;

  readonly OPTIMIERUNG_VOLLEINSPEISUNG: Optimierung = 'volleinspeisung';
  readonly OPTIMIERUNG_INDIVIDUELL: Optimierung = 'individuell';

  /**
   * maximum width in pixel of the whole area
   */
  readonly AREA_WIDTH_MAX = 720;
  readonly AREA_START_X = 100;
  readonly AREA_MIN_WITH = 230;

  /**
   * thumb start position
   */
  readonly THUMB_START = -1440;

  /**
   * steps to change the battery size
   */
  readonly BATTERY_CHANGE_STEP = 1;

  /**
   * svg original width
   */
  readonly SVG_WITH = 980;

  /**
   * List of placeholders for all texts
   */
  placeholder: {[key: string]: string | number} = {};

  /**
   * Device matrix will calculate withs and sizes of parts of the result image
   */
  deviceMatrix!: DeviceMatrix;

  /**
   * position of the both thumbs, if areas can be changed.
   */
  thumbs: Thumb;

  /**
   * Element to drag
   */
  dragElement?: string;

  /**
   * Minimum area that is needed for the own use
   */
  ownuseMin: number = 0;

  /**
   * Minimum area that is needed for the fed in
   */
  feedinMin: number = 0;

  /**
   * Size of the battery
   */
  batterySize: number = 0;

  /**
   * minimum for the battery
   */
  batteryMin: number = 1;

  /**
   * maximum for the battery
   */
  batteryMax: number = 20;

  /**
   * factor between px on the grafic and power of the area when resizing the areas.
   */
  pxToKwp: number = 0;

  /**
   * Steps of the thumbs in px. Will be recalculated on window resize and initially
   */
    thumbStep: number = 5;

  /**
   * constructor
   * @param decimalPipe
   */
  constructor(private decimalPipe: DecimalPipe) {}

  /**
   * @inheritdoc
   */
  ngOnInit() {
    // init thum data, will be overwritten later
    this.thumbs = {
      ownuse: {x: this.THUMB_START, max: 0, min: 0},
      feedin: {x: this.THUMB_START, max: 0, min: 0}
    };

    this.updateValues();
    this.updateWidthFactors();
  }

  /**
   * @inheritdoc
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if(changes.currentConfigurationType && changes.currentConfigurationType.isFirstChange() === false ||
    changes.result && changes.result.isFirstChange() === false) {
      this.updateValues();
    }
  }

  /**
   * set the placeholder to the current configuration.
   */
  updateValues(): void {
    // get all placeholders
    this.placeholder = this.result.result.placeholder[this.currentConfigurationType] ?? {};

    // should we set a min width of the areas?
    const setMinWidth = this.currentConfigurationType !== this.OPTIMIERUNG_INDIVIDUELL &&
      this.currentConfigurationType !== this.OPTIMIERUNG_VOLLEINSPEISUNG;

    // set width of both device parts
    const area = (this.placeholder.flaeche_verfuegbar_plain as number);
    const widthEigennutzung = (this.placeholder.flaeche_plain ?? 0) as number / area;
    const widthVolleinspeisung = (this.placeholder.flaeche_volleinspeisung_plain ?? 0) as number / area;

    // get battery size and min/max values
    this.batterySize = this.placeholder.batteriespeicher_kapazitaet_plain as number ?? this.batterySize;
    this.batteryMin = this.placeholder.batteriespeicher_kapazitaet_min_plain as number > 0 ?
      this.placeholder.batteriespeicher_kapazitaet_min_plain as number : this.batteryMin;
    this.batteryMax = this.placeholder.batteriespeicher_kapazitaet_max_plain as number > 0 ?
      this.placeholder.batteriespeicher_kapazitaet_max_plain as number : this.batteryMax;

    // (re)ralculate the device matrix
    this.deviceMatrix = {
      eigennutzung: {
        visible: widthEigennutzung > 0,
        x: this.AREA_START_X,
        width: this.AREA_WIDTH_MAX * widthEigennutzung,
      },
      volleinspeisung: {
        visible: widthVolleinspeisung > 0,
        x: this.AREA_START_X + (this.AREA_WIDTH_MAX * widthEigennutzung),
        width: this.AREA_WIDTH_MAX * widthVolleinspeisung,
      }
    };

    // check if own use area is big enough for the text
    if(setMinWidth && this.deviceMatrix.eigennutzung.visible &&
      this.deviceMatrix.eigennutzung.width < this.AREA_MIN_WITH) {
      const diff = this.AREA_MIN_WITH - this.deviceMatrix.eigennutzung.width;
      this.deviceMatrix.eigennutzung.width = this.AREA_MIN_WITH;

      if(this.deviceMatrix.volleinspeisung.visible === true) {
        this.deviceMatrix.volleinspeisung.x = this.AREA_START_X + this.deviceMatrix.eigennutzung.width;
        this.deviceMatrix.volleinspeisung.width = this.deviceMatrix.volleinspeisung.width - diff;
      }
    }

    // check if feedin areas is big enough for the text
    if(setMinWidth && this.deviceMatrix.volleinspeisung.visible === true &&
      this.deviceMatrix.volleinspeisung.width < this.AREA_MIN_WITH) {
      const diff = this.AREA_MIN_WITH - this.deviceMatrix.volleinspeisung.width;
      this.deviceMatrix.volleinspeisung.width = this.AREA_MIN_WITH;

      if(this.deviceMatrix.eigennutzung.visible === true) {
        if(this.deviceMatrix.eigennutzung.width + this.deviceMatrix.volleinspeisung.width > this.AREA_WIDTH_MAX) {
          this.deviceMatrix.eigennutzung.width = this.deviceMatrix.eigennutzung.width - diff;
        }
        this.deviceMatrix.volleinspeisung.x = this.AREA_START_X + this.deviceMatrix.eigennutzung.width;
      }
    }

    // (re)calculate the min/max values for the thumbs on the individual graphic
    if(this.currentConfigurationType === this.OPTIMIERUNG_INDIVIDUELL ||
      this.currentConfigurationType === this.OPTIMIERUNG_VOLLEINSPEISUNG) {
      const system = this.result.result.data.photovoltaic_systems.find(system => system.optimierung === this.currentConfigurationType);

      this.ownuseMin = system.eigennutzung?.flaeche_min ?? this.ownuseMin;
      this.feedinMin = system.volleinspeisung?.flaeche_min ?? this.feedinMin;
      const area = this.result.result.placeholder[this.currentConfigurationType].flaeche_verfuegbar_plain ?? 0;

      const ownUseMinPercent = this.ownuseMin / area;
      const feedinMinPercent = this.currentConfigurationType === this.OPTIMIERUNG_VOLLEINSPEISUNG ? this.feedinMin / area : 0;

      // set thumb position and min/max
      this.thumbs.ownuse.x = this.THUMB_START + this.deviceMatrix.eigennutzung.width;
      this.thumbs.ownuse.min = this.THUMB_START + (this.AREA_WIDTH_MAX * ownUseMinPercent);
      this.thumbs.ownuse.max = this.AREA_WIDTH_MAX + this.THUMB_START;
      this.thumbs.feedin.x = this.THUMB_START + this.deviceMatrix.eigennutzung.width + this.deviceMatrix.volleinspeisung.width;
      this.thumbs.feedin.min = this.THUMB_START + this.deviceMatrix.eigennutzung.width + (this.AREA_WIDTH_MAX * feedinMinPercent);
      this.thumbs.feedin.max = this.AREA_WIDTH_MAX - this.deviceMatrix.eigennutzung.width +
        this.THUMB_START + this.deviceMatrix.eigennutzung.width;
    }
  }

  @HostListener('window:resize', [])
  updateWidthFactors(): void {
    const power = this.placeholder.leistung_gesamt_plain as number;
    const area = this.placeholder.flaeche_gesamt_plain as number;
    const areaPx = this.deviceMatrix.eigennutzung.width + this.deviceMatrix.volleinspeisung.width;
    const pxToM2 = areaPx / area;

    this.pxToKwp = areaPx / power;
    this.thumbStep = pxToM2 * .5;
  }

  /**
   * start dagging a thumb
   * @param event
   * @param thumb
   */
  startDrag(event: Event, thumb: string): void {
    event.preventDefault();
    // set moving thumb
    this.dragElement = thumb;
  }

  /**
   * stop dragging the thumb
   * @param event
   */
  @HostListener('document:mouseup', [])
  @HostListener('document:touchend', [])
  stopDrag(event: any): void {
    if(!this.dragElement) {
      // no drag active
      return;
    }

    this.dragElement = null;

    this.recaluclate();
  }

  /**
   * recalculate the system with the current adaptions
   */
  recaluclate(): void {
    // recalculate
    const ownUsePercentage = this.deviceMatrix.eigennutzung.width / this.AREA_WIDTH_MAX;
    const feedinPercentage = this.deviceMatrix.volleinspeisung.width / this.AREA_WIDTH_MAX;

    this.areaChanged.emit({feedin: feedinPercentage, ownuse: ownUsePercentage, battery: this.batterySize});
  }

  /**
   * moving the mouse (while dragging?)
   * @param event
   */
  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onMouseMove(event: any) {
    if(!this.dragElement) {
      return;
    }

    // calculate new x value for the moving thumb
    const touch = event.touches ? event.touches[0] : event;
    const rect = this.svgElement.nativeElement.getBoundingClientRect();
    const widthFactor = rect.width / this.SVG_WITH;
    let x = ((touch.clientX - rect.left) / widthFactor) - this.AREA_START_X + this.THUMB_START;

    if(x > this.thumbs[this.dragElement].min && x < this.thumbs[this.dragElement].max) {
      // rounding for complete steps
      x = Math.round(x / this.thumbStep) * this.thumbStep;

      this.thumbs[this.dragElement].x = x;

      this.setAreaSizes(x);

      // preview of the new size
      this.placeholder.leistung = this.decimalPipe.transform(this.deviceMatrix.eigennutzung.width / this.pxToKwp, '1.1-1');
      // preview of the new size
      this.placeholder.leistung_volleinspeisung = this.decimalPipe.transform(
        this.deviceMatrix.volleinspeisung.width / this.pxToKwp,
        '1.1-1'
      );
    }
    else if(x < this.thumbs[this.dragElement].min) {
      this.thumbs[this.dragElement].x = this.thumbs[this.dragElement].min;
      // below the minimum. Set size to 0
      this.setAreaSizes(this.thumbs[this.dragElement].x);
    }
    else if(x > this.thumbs[this.dragElement].max) {
      this.thumbs[this.dragElement].x = this.thumbs[this.dragElement].max;
      this.setAreaSizes(this.thumbs[this.dragElement].x);
    }
  }

  /**
   * Set the new area sizes, depending on the x position of the thumb
   * @param x
   */
  setAreaSizes(x: number): void {
    // move feedin thumb
    if(this.dragElement === 'feedin') {
      this.deviceMatrix.volleinspeisung.width = x + Math.abs(this.THUMB_START) + this.AREA_START_X - this.deviceMatrix.volleinspeisung.x;
    }

    // move own use thumb
    if(this.dragElement === 'ownuse') {
      this.deviceMatrix.eigennutzung.width = x + Math.abs(this.THUMB_START) + this.AREA_START_X - this.deviceMatrix.eigennutzung.x;
      this.deviceMatrix.volleinspeisung.x = this.deviceMatrix.eigennutzung.width + this.AREA_START_X;
      this.thumbs.feedin.x = x + this.deviceMatrix.volleinspeisung.width;

      // check if we have to reduce the width of the fedin area
      const diffToMax = this.AREA_WIDTH_MAX - (
        this.deviceMatrix.volleinspeisung.x + this.deviceMatrix.volleinspeisung.width - this.AREA_START_X
      );
      if(diffToMax < 0) {
        this.deviceMatrix.volleinspeisung.width = this.deviceMatrix.volleinspeisung.width + diffToMax;
      }
    }
  }

  /**
   * Resize (raise/lower) the battery size
   * @param direction
   * @param event
   */
  resizeBattery(direction: '+' | '-', event: Event): void {
    event.preventDefault();

    if(direction === '+') {
      // raise
      if(this.batterySize % this.BATTERY_CHANGE_STEP !== 0) {
        this.batterySize = Math.ceil(this.batterySize);
      }
      else {
        this.batterySize += this.BATTERY_CHANGE_STEP;
      }

      if(this.batterySize > this.batteryMax) {
        this.batterySize = this.batteryMax;
      }
    }
    else {
      // lower
      if(this.batterySize % this.BATTERY_CHANGE_STEP !== 0) {
        this.batterySize = Math.floor(this.batterySize);
      }
      else {
        this.batterySize -= this.BATTERY_CHANGE_STEP;
      }

      if(this.batterySize < this.batteryMin) {
        this.batterySize = 0;
      }
    }

    this.recaluclate();
  }

  /**
   * Handle keyboard navigation for sliders
   * @param type The slider type ('feedin' or 'ownuse')
   * @param event The keyboard event
   */
  handleSliderKeydown(type: string, event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();

    // Set the drag element to the current slider
    this.dragElement = type;

    // Calculate the current position
    let currentX = this.thumbs[type].x;

    let changeDone = true;

    // Move left or right based on arrow key
    if (event.key === 'ArrowLeft') {
      // Decrease value (move left)
      currentX -= this.thumbStep * 2;
      if (currentX < this.thumbs[type].min) {
        currentX = this.thumbs[type].min;
        changeDone = false;
      }
    } else if (event.key === 'ArrowRight') {
      // Increase value (move right)
      currentX += this.thumbStep * 2;
      if (currentX > this.thumbs[type].max) {
        currentX = this.thumbs[type].max;
        changeDone = false;
      }
    }

    if (changeDone === false) {
      // sto here, do not need to recalculate
      return;
    }

    // Update the thumb position
    this.thumbs[type].x = currentX;

    // Update the area sizes
    this.setAreaSizes(currentX);

    // Update the preview values
    this.placeholder.leistung = this.decimalPipe.transform(this.deviceMatrix.eigennutzung.width / this.pxToKwp, '1.1-1');
    this.placeholder.leistung_volleinspeisung = this.decimalPipe.transform(
      this.deviceMatrix.volleinspeisung.width / this.pxToKwp,
      '1.1-1'
    );

    // Recalculate after the change
    this.recaluclate();

    // Reset drag element
    this.dragElement = null;
  }
}
